import { API_BASE_URL, api } from './client'
import type { WarehousePositionStockItem } from '@/lib/api/warehouse-position-api'
import type { InOutStatsResult } from '@/lib/api/inventory-stats-api'
import type { CareLog, CareLogArea } from '@/lib/api/care-logs-api'

export interface DateRangeParams {
    date_from?: string
    date_to?: string
}

export type ReportPaymentMethod = 'cash' | 'bank_transfer'

export interface RevenueSummary {
    total_revenue: number
    total_orders: number
    average_order_value: number
    paid_amount: number
    unpaid_amount: number
    period_label?: string
}

export interface RevenueByTimeItem {
    period: string
    revenue: number
    order_count: number
}

export interface RevenueByProductItem {
    product_id: string
    product_name: string
    product_sku: string
    revenue: number
    quantity_sold: number
    order_count: number
}

export interface RevenueByCustomerItem {
    customer_id: string
    customer_name: string
    revenue: number
    order_count: number
}

export interface RevenueByChannelItem {
    channel: string
    revenue: number
    order_count: number
    percentage: number
}

export interface RevenueByProvinceItem {
    province_code: string
    province: string
    revenue: number
    order_count: number
    percentage: number
    is_other: boolean
}

export interface RevenueByWardItem {
    ward_code: string
    ward: string
    revenue: number
    order_count: number
    percentage: number
    is_other: boolean
}

export interface ProvinceOrderItem {
    order_id: string
    order_code: string
    customer_name: string
    revenue: number
    delivered_at: string
}

export interface OrdersSummary {
    total_orders: number
    by_status: Record<string, number>
    delivery_rate: number
}

export interface DebtSummary {
    total_unpaid: number
    total_partial: number
    total_overdue: number
    customer_count: number
}

export interface MethodAmount {
    method: string
    amount: number
}

export interface CashFlowResponse {
    inflow_by_method: MethodAmount[]
    outflow_by_method: MethodAmount[]
    total_inflow: number
    total_outflow: number
    net: number
    cash_net: number
    bank_net: number
    bank_actual: number
    bank_recorded: number
    bank_difference: number
}

/** Body của POST /reports/render/{format} — xem reportrender.Document bên server. */
export interface RenderReportDocument {
    title: string
    subtitle?: string
    subject?: string
    created_label: string
    file_name: string
    link_first_column: boolean
    columns: {
        key: string
        label: string
        align: 'left' | 'right' | 'center'
        width: number
        format: 'text' | 'number' | 'decimal'
        wrap?: boolean
    }[]
    summary?: { cells: Record<string, string | number | null> }
    rows: {
        cells: Record<string, string | number | null>
        detail?: boolean
        /** Chữ sau ô đầu, màu chữ thường (vd "(Thu nợ)"). */
        first_suffix?: string
    }[]
}

/** Một vị trí kho trên báo cáo Tồn theo vị trí; position_id null = chưa xếp vị trí. */
export interface PositionStockSummaryRow {
    position_id: string | null
    code: string
    name: string
    applicable_lot_types: ('product' | 'packaging')[]
    is_active: boolean
    product_items: number
    product_lots: number
    packaging_items: number
    packaging_lots: number
}

/** Kho có báo cáo tồn theo phân loại. */
export type LotStockKind = 'sorting' | 'scenting'

/** Một phiếu nhập/xuất (đã duyệt) có lô thuộc phân loại, kèm từng lô. */
export interface LotGradeNote {
    note_id: string
    code: string
    direction: 'in' | 'out'
    occurred_at: string
    quantity: number
    lots: { lot_code: string; quantity: number }[]
}

export interface LotGradeNotesResponse {
    notes: LotGradeNote[]
    total_in: number
    total_out: number
}

/** Bộ lọc báo cáo nhật ký chăm sóc (ngày theo giờ wall-clock như màn Nhật ký). */
export interface CareLogReportParams extends DateRangeParams {
    activity_type?: string
}

/** Số lần chăm sóc trong kỳ của một hộ, tách theo loại hoạt động. */
export interface CareLogGrowerRow {
    grower_id: string
    grower_name: string
    nickname: string
    total: number
    by_activity: Record<string, number>
}

export interface GrowerReportArea {
    code: string
    name: string
    acreage: number
}

/** Hộ trồng (chưa xoá) kèm vùng trồng — chỉ thông tin liên hệ. */
export interface GrowerReportRow {
    id: string
    name: string
    nickname: string
    phone: string
    address: string
    areas: GrowerReportArea[]
}

/** Bộ lọc Báo cáo bán hàng; method/channel là danh sách cách nhau dấu phẩy. */
/** Báo cáo thu chi (M-85): kind = income/expense, method — cách nhau dấu phẩy. */
export interface CashbookReportParams extends DateRangeParams {
    kind?: string
    method?: string
    /** Id người tạo, cách nhau dấu phẩy (M-143). */
    created_by?: string
}

/** Một ngày của Báo cáo thu chi; net = income − expense. */
export interface CashbookByDayRow {
    date: string
    income: number
    income_count: number
    expense: number
    expense_count: number
    net: number
}

export interface CashbookByDayResponse {
    rows: CashbookByDayRow[]
    totals: Omit<CashbookByDayRow, 'date'>
}

/** Một dòng hàng của đơn (tên, SL, đơn vị) — Báo cáo bán hàng, Thu chi. */
export interface ReportOrderItem {
    product_name: string
    quantity: number
    unit: string
}

/** Một phiếu thu hoặc chi (cùng quy tắc màn Dòng tiền). */
export interface CashbookEntry {
    /**
     * order_payment = khoản thu nhập trên đơn; receipt = phiếu thu cấp khách
     * (cả phiếu, gồm phần khách trả dư); expense =
     * khoản chi (đã hoặc chưa thanh toán, M-143); driver_remittance = tiền tài xế thu hộ một đơn, kế
     * toán đã nhận (M-133), ghi vào lúc nhận.
     */
    kind: 'order_payment' | 'receipt' | 'expense' | 'driver_remittance'
    side: 'income' | 'expense'
    /** Mã đơn / mã phiếu hoàn; phiếu thu và khoản chi: 8 ký tự cuối id. */
    code: string
    occurred_at: string
    /** UUID rỗng nếu không gắn đơn. */
    order_id: string
    /** UUID rỗng với khoản chi. */
    customer_id: string
    /** Tên khách hoặc nhà cung cấp. */
    counterparty: string
    /** Danh mục khoản chi. */
    category: string
    payment_method: string
    income: number
    expense: number
    /** Phần khách trả dư của phiếu thu (đã nằm trong income). */
    prepaid: number
    /** Dòng driver_remittance: tài xế đã thu hộ. */
    driver_name?: string
    /**
     * Dòng driver_remittance: khách trả tài xế bằng gì (cash | bank_transfer
     * vào TK tài xế); payment_method là cách tài xế đưa lại tiền cho HTX.
     */
    customer_method?: string
    /** Khoản chi: paid | unpaid (chưa trả vẫn cộng vào chi); dòng thu: ''. */
    payment_status?: '' | 'paid' | 'unpaid'
    /** Id khoản chi (mã phiếu chi mở chi tiết, M-143); UUID rỗng với dòng thu. */
    expense_id?: string
    /** Người tạo dòng (M-143); tờ phiếu bỏ cột này từ M-168, vẫn lọc được. */
    creator_name?: string
    /** Ghi chú của khoản chi (cột "Nội dung", M-168); dòng thu: ''. */
    note?: string
    /**
     * Dòng hàng của đơn (M-158), như Báo cáo bán hàng: chỉ dòng order_payment /
     * driver_remittance gắn một đơn; dòng khác rỗng. Chỉ để hiển thị.
     */
    items?: ReportOrderItem[]
}

export interface CashbookTransactionsResponse {
    rows: CashbookEntry[]
    count: number
    income: number
    expense: number
    net: number
}

export interface SalesReportParams extends DateRangeParams {
    method?: string
    channel?: string
    /** cash, bank_transfer, cod_via_carrier, unpaid — cách nhau dấu phẩy. */
    payment?: string
}

/**
 * Một ngày (M-78, tính theo tiền thu): revenue = tiền thu trong ngày (đơn tạo
 * trong ngày + thu nợ đơn cũ), returns = tiền hoàn cho khách (dương), net.
 */
export interface SalesByDayRow {
    date: string
    revenue: number
    order_count: number
    returns: number
    return_count: number
    /** Giữ cho tương thích API, luôn 0 từ M-78. */
    return_fees: number
    /**
     * Tiền khách trả dư trên phiếu thu (thành số dư trả trước, M-85): tiền
     * thu nhưng chưa bán gì nên không nằm trong revenue/net.
     */
    prepaid: number
    prepaid_count: number
    net: number
}

export interface SalesByDayResponse {
    rows: SalesByDayRow[]
    totals: Omit<SalesByDayRow, 'date'>
}

export interface SalesTransaction {
    order_id: string
    order_code: string
    /**
     * sale = đơn tạo trong kỳ; debt_collection = đơn tạo ngoài kỳ có tiền thu
     * trong kỳ ("Thu nợ"); refund = hoàn tiền cho khách (order_id rỗng nếu phiếu
     * hoàn không gắn đơn, order_code là mã phiếu); prepaid = phần khách trả
     * dư trên phiếu thu (M-85, order_id rỗng, order_code = 8 ký tự cuối mã phiếu).
     */
    kind: 'sale' | 'debt_collection' | 'refund' | 'prepaid'
    occurred_at: string
    /** Khách của đơn (phiếu hoàn không gắn đơn: khách của phiếu). */
    customer_id: string
    customer_name: string
    /** Tiền thu trong kỳ (0 nếu chưa thu); âm với dòng hoàn tiền. */
    amount: number
    /** Giữ cho tương thích API, luôn 0 từ M-78. */
    fee_amount: number
    /**
     * Chỉ dòng sale: '' đã thu đủ trong kỳ, còn nợ đến hôm nay, đã trả hết sau
     * kỳ, đơn đã hoàn hàng hoặc đã huỷ.
     */
    debt_label: '' | 'debt_open' | 'debt_settled' | 'returned' | 'cancelled'
    /** Tiền khách trả bằng số dư trả trước trong kỳ (không nằm trong amount). */
    credit_amount: number
    /** Số đơn còn nợ hiện tại (không âm; 0 với dòng hoàn tiền). */
    remaining_amount: number
    /** Chỉ dòng prepaid: tiền khách trả dư (không nằm trong amount). */
    prepaid_amount: number
    items: ReportOrderItem[]
}

export interface SalesTransactionsResponse {
    rows: SalesTransaction[]
    count: number
    total: number
    /** Cộng prepaid_amount, tách khỏi total. */
    prepaid_total: number
}

/** Vị trí HIỆN TẠI của lô đã xuất (M-130); `position_id` null = chưa gán vị trí. */
export interface StockOutPosition {
    position_id: string | null
    position_code: string
    position_name: string
}

/**
 * Báo cáo xuất kho theo sản phẩm (M-70): PXK bán hàng đã duyệt trong kỳ. Một
 * dòng = (sản phẩm, vị trí); cùng sản phẩm nằm liền nhau.
 */
export interface StockOutProductRow extends StockOutPosition {
    product_id: string
    product_sku: string
    product_name: string
    unit: string
    quantity: number
    order_count: number
}

export interface StockOutByProductResponse {
    rows: StockOutProductRow[]
    /** order_count: mỗi đơn đếm một lần trên cả kỳ; product_count: số sản phẩm khác nhau. */
    totals: { order_count: number; product_count: number }
}

/** Không có product_id = mọi sản phẩm (Xuất tất cả). */
export interface StockOutOrdersParams extends DateRangeParams {
    product_id?: string
}

/** Một bộ (đơn, sản phẩm, vị trí); nhiều PXK của cùng bộ đã gộp. */
export interface StockOutOrderRow extends StockOutPosition {
    order_id: string
    order_code: string
    stocked_out_at: string
    customer_name: string
    note_codes: string
    product_id: string
    product_sku: string
    product_name: string
    unit: string
    quantity: number
}

export interface StockOutOrdersResponse {
    rows: StockOutOrderRow[]
    count: number
    order_count: number
    total_quantity: number
}

/** Bộ lọc báo cáo công nợ theo kỳ (Báo cáo → Công nợ). */
export interface DebtPeriodParams extends DateRangeParams {
    q?: string
    closing_min?: number
    closing_max?: number
}

/** Một khách: Nợ cuối kỳ = Nợ đầu kỳ + Ghi nợ − Ghi có. */
export interface DebtPeriodRow {
    customer_id: string
    customer_code: string
    customer_name: string
    phone: string
    opening: number
    debit: number
    credit: number
    closing: number
}

export interface DebtPeriodResponse {
    rows: DebtPeriodRow[]
    totals: {
        customer_count: number
        opening: number
        debit: number
        credit: number
        closing: number
    }
}

export interface CustomerDebtLedgerRow {
    id: string
    occurred_at: string
    type: string
    reference_code: string
    sales_order_id?: string
    note: string
    debit: number
    credit: number
    balance: number
}

export interface CustomerDebtLedgerResponse {
    customer: { id: string; code: string; name: string; phone: string }
    opening: number
    rows: CustomerDebtLedgerRow[]
    total_debit: number
    total_credit: number
    closing: number
}

export interface InventorySummary {
    total_skus: number
    total_stock_value: number
    low_stock_count: number
    out_of_stock_count: number
}

export interface InventoryDetailItem {
    product_id: string
    product_name: string
    product_sku: string
    quantity: number
    min_stock_level: number
    cost_price: number
    stock_value: number
    status: 'ok' | 'low' | 'out'
}

/** Tồn khả dụng của một sản phẩm tại một vị trí kho, đã gộp mọi lô (và mọi mức
 * chiết khấu) đứng ở vị trí đó. `position_code`/`position_name` null khi lô chưa
 * được gán vị trí — tồn của nó vẫn phải hiện, gộp thành một dòng "chưa gán". */
export interface InventoryPositionItem {
    product_id: string
    position_code: string | null
    position_name: string | null
    quantity: number
}

/** Ba chặng chế biến, THỨ TỰ CỐ ĐỊNH theo dòng chảy sản xuất. Màu biểu đồ gán
 * theo đúng thứ tự này (cơ chế an toàn cho người mù màu) nên đừng sắp lại. */
export type YieldLossStage = 'sorting' | 'scenting' | 'packaging'

/** Số liệu hao hụt của một chặng trong một kỳ (hoặc cả kỳ lọc khi ở `totals`).
 *
 * `loss_pct` là `null` khi `used_kg <= 0` — kỳ không có phiếu, hoặc phiếu hoàn
 * rơi lệch kỳ. Phải hiện "—", TUYỆT ĐỐI không hiển thị thành 0 %. */
export interface YieldLossStageEntry {
    stage: YieldLossStage
    source_kg: number
    returned_kg: number
    used_kg: number
    output_kg: number
    loss_kg: number
    loss_pct: number | null
    note_count: number
    return_note_count: number
    missing_weight_note_count: number
}

/** Một ô thời gian. `from`/`to` là ranh giới ngày do backend chốt; popup gọi
 * lại bằng đúng hai mốc này thay vì tự tính biên tuần/tháng. */
export interface YieldLossBucket {
    key: string
    label: string
    from: string
    to: string
    stages: YieldLossStageEntry[]
}

export interface YieldLossResponse {
    group_by: 'day' | 'week' | 'month'
    totals: YieldLossStageEntry[]
    buckets: YieldLossBucket[]
}

export interface YieldLossNoteEntry {
    id: string
    code: string
    approved_at: string
    /** kg nguồn khai trong phiếu, CHƯA trừ hoàn. */
    source_kg: number
    output_kg: number
    loss_pct: number | null
    has_missing_weight: boolean
}

export interface YieldLossNotesResponse {
    stage: YieldLossStage
    source_kg: number
    returned_kg: number
    used_kg: number
    output_kg: number
    loss_kg: number
    loss_pct: number | null
    note_count: number
    return_note_count: number
    missing_weight_note_count: number
    truncated: boolean
    notes: YieldLossNoteEntry[]
}

export interface YieldLossParams {
    from?: string
    to?: string
    group_by?: 'day' | 'week' | 'month'
}

export interface YieldLossNotesArgs {
    stage: YieldLossStage
    from: string
    to: string
}

/** Trạng thái tồn dùng để lọc — khác `InventoryDetailItem['status']` vì đây là
 * bộ lọc đầu vào ("còn hàng" gộp mọi SKU có tồn > 0), không phải nhãn hiển thị.
 *
 * `low` = còn hàng nhưng đã chạm ngưỡng `products.min_stock_level`. Cùng ngưỡng
 * mà dịch vụ đọc loa (`service/stocklow`) dùng, nên loa và báo cáo nói về cùng
 * một tập sản phẩm. Ngưỡng 0 = không theo dõi sản phẩm đó. */
export type InventoryStockFilter = 'in_stock' | 'low' | 'out'

/** Bộ lọc dùng chung cho bảng chi tiết, thẻ KPI và bản xuất Excel — ba chỗ này
 * phải nói về cùng một tập dữ liệu. */
export interface InventoryFilterParams {
    status?: InventoryStockFilter
    /** Tìm không dấu theo tên sản phẩm hoặc SKU. */
    search?: string
}

export interface InventoryDetailsParams extends InventoryFilterParams {
    limit?: number
}

export interface StaffPerformanceItem {
    user_id: string
    user_name: string
    revenue: number
    order_count: number
    avg_order_value: number
}

export interface DeliveryPerformance {
    total_delivered: number
    avg_delivery_hours: number
    sla_breach_count: number
    sla_breach_rate: number
    on_time_rate: number
}

export interface DriverPerformanceItem {
    driver_id: string
    driver_name: string
    delivered_count: number
    avg_delivery_hours: number
    sla_breach_count: number
}

export interface DefectItem {
    period: string
    product_id: string
    product_name: string
    quantity: number
    note_count: number
}

export interface MovementByTimeItem {
    period: string
    stock_in: number
    stock_out: number
}

export interface MovementBreakdownPeriodRow {
    period: string
    stock_in: number
    stock_out: number
    // Map product_id → qty cho period đó. Chỉ chứa SP trong top_entities.
    breakdown_in: Record<string, number>
    breakdown_out: Record<string, number>
}

export interface MovementBreakdownEntity {
    id: string
    sku: string
    name: string
    total_in: number
    total_out: number
}

export interface MovementBreakdownResult {
    rows: MovementBreakdownPeriodRow[]
    top_entities: MovementBreakdownEntity[]
}

export interface TopMoverItem {
    product_id: string
    product_name: string
    product_sku: string
    in_qty: number
    out_qty: number
    net_change: number
}

export interface RevenueDelta {
    revenue: number
    orders: number
    avg_order: number
    customers: number
}

export interface CompareRevenueSummaryResponse {
    current: RevenueSummary
    previous: RevenueSummary
    deltas: RevenueDelta
}

export const reportApi = {
    revenueSummary: (params?: DateRangeParams) =>
        api.get<RevenueSummary>('/reports/revenue/summary', { params }),

    compareRevenueSummary: (params?: DateRangeParams) =>
        api.get<CompareRevenueSummaryResponse>('/reports/revenue/compare', {
            params,
        }),

    revenueByTime: (
        params?: DateRangeParams & { group_by?: 'day' | 'week' | 'month' },
    ) => api.get<RevenueByTimeItem[]>('/reports/revenue/by-time', { params }),

    revenueByProduct: (params?: DateRangeParams & { limit?: number }) =>
        api.get<RevenueByProductItem[]>('/reports/revenue/by-product', {
            params,
        }),

    revenueByCustomer: (params?: DateRangeParams & { limit?: number }) =>
        api.get<RevenueByCustomerItem[]>('/reports/revenue/by-customer', {
            params,
        }),

    revenueByChannel: (params?: DateRangeParams) =>
        api.get<RevenueByChannelItem[]>('/reports/revenue/by-channel', {
            params,
        }),

    revenueByProvince: (params?: DateRangeParams & { limit?: number }) =>
        api.get<RevenueByProvinceItem[]>('/reports/revenue/by-province', {
            params,
        }),

    ordersByProvince: (params: DateRangeParams & { province_code: string }) =>
        api.get<ProvinceOrderItem[]>('/reports/revenue/by-province/detail', {
            params,
        }),

    revenueByWard: (
        params: DateRangeParams & { province_code: string; limit?: number },
    ) =>
        api.get<RevenueByWardItem[]>('/reports/revenue/by-ward', {
            params,
        }),

    ordersSummary: (params?: DateRangeParams) =>
        api.get<OrdersSummary>('/reports/orders/summary', { params }),

    debtSummary: (params?: DateRangeParams) =>
        api.get<DebtSummary>('/reports/debts/summary', { params }),

    cashFlow: (
        params?: DateRangeParams & { payment_method?: ReportPaymentMethod },
    ) =>
        api.get<CashFlowResponse>('/reports/cash-flow', {
            params: params as
                | Record<string, string | number | boolean | undefined>
                | undefined,
        }),

    /** Dàn trang bảng báo cáo (khung xem) ra PDF A4 hoặc Excel. */
    renderReport: (format: 'pdf' | 'xlsx', doc: RenderReportDocument) =>
        api.postBlob(`/reports/render/${format}`, doc),

    positionStockSummary: () =>
        api.get<{ rows: PositionStockSummaryRow[] }>(
            '/reports/positions/stock',
        ),

    /** Tồn từng mặt hàng ở một vị trí — cùng dữ liệu màn Vị trí kho, quyền reports.read. */
    positionStock: (positionId: string) =>
        api.get<WarehousePositionStockItem[]>(
            `/reports/positions/${positionId}/stock`,
        ),

    /** Nhập/xuất/tồn theo phân loại — cùng usecase màn Thống kê kho, quyền reports.read. */
    lotStock: (
        kind: LotStockKind,
        params: { from: string; to: string; group_by: string },
    ) =>
        api.get<InOutStatsResult>(`/reports/lot-stock/${kind}`, {
            params: { period: 'custom', ...params },
        }),

    lotGradeNotes: (
        kind: LotStockKind,
        gradeId: string,
        params: DateRangeParams,
    ) =>
        api.get<LotGradeNotesResponse>(
            `/reports/lot-stock/${kind}/grades/${gradeId}/notes`,
            { params },
        ),

    careLogSummary: (params: CareLogReportParams) =>
        api.get<{ rows: CareLogGrowerRow[] }>('/reports/care-logs/growers', {
            params,
        }),

    /** Mọi nhật ký của một hộ trong kỳ — cùng dữ liệu màn Nhật ký chăm sóc. */
    careLogGrowerLogs: (growerId: string, params: CareLogReportParams) =>
        api.get<CareLog[]>(`/reports/care-logs/growers/${growerId}/logs`, {
            params,
        }),

    /** Vùng trồng của một hộ — nguồn ô lọc vùng ở tầng nhật ký của hộ. */
    careLogGrowerAreas: (growerId: string) =>
        api.get<CareLogArea[]>(`/reports/care-logs/growers/${growerId}/areas`),

    growers: () => api.get<{ rows: GrowerReportRow[] }>('/reports/growers'),

    cashbookByDay: (params: CashbookReportParams) =>
        api.get<CashbookByDayResponse>('/reports/cashbook/by-day', {
            params,
        }),

    cashbookTransactions: (params: CashbookReportParams) =>
        api.get<CashbookTransactionsResponse>(
            '/reports/cashbook/transactions',
            { params },
        ),

    salesByDay: (params: SalesReportParams) =>
        api.get<SalesByDayResponse>('/reports/sales/by-day', { params }),

    salesTransactions: (params: SalesReportParams) =>
        api.get<SalesTransactionsResponse>('/reports/sales/transactions', {
            params,
        }),

    stockOutByProduct: (params: DateRangeParams) =>
        api.get<StockOutByProductResponse>('/reports/stock-out/by-product', {
            params,
        }),

    stockOutOrders: (params: StockOutOrdersParams) =>
        api.get<StockOutOrdersResponse>('/reports/stock-out/orders', {
            params,
        }),

    debtByCustomerPeriod: (params: DebtPeriodParams) =>
        api.get<DebtPeriodResponse>('/reports/debts/customers', { params }),

    customerDebtLedgerPeriod: (customerId: string, params: DateRangeParams) =>
        api.get<CustomerDebtLedgerResponse>(
            `/reports/debts/customers/${customerId}/ledger`,
            { params },
        ),

    /** Không truyền params = toàn kho (dashboard); truyền = cùng bộ lọc với bảng. */
    inventorySummary: (params?: InventoryFilterParams) =>
        api.get<InventorySummary>('/reports/inventory/summary', { params }),

    inventoryDetails: (params?: InventoryDetailsParams) =>
        api.get<InventoryDetailItem[]>('/reports/inventory/details', {
            params,
        }),

    /** Tồn theo vị trí của một sản phẩm — dòng bung ra trên bảng tồn kho.
     *
     * Cố ý nằm dưới `/reports` (quyền `reports.read`) chứ không dùng
     * `/products/:id/stock-breakdown`: endpoint kia đòi `products.read`, role
     * Kế toán không có nên bung dòng sẽ ăn 403. */
    inventoryPositions: (productId: string) =>
        api.get<InventoryPositionItem[]>('/reports/inventory/positions', {
            params: { product_id: productId },
        }),

    yieldLoss: (params?: YieldLossParams) =>
        api.get<YieldLossResponse>('/reports/yield-loss', { params }),

    /** Danh sách phiếu của một ô. `from`/`to` phải là đúng hai mốc bucket trả
     * về, không tự tính lại. */
    yieldLossNotes: (args: YieldLossNotesArgs) =>
        api.get<YieldLossNotesResponse>('/reports/yield-loss/notes', {
            params: args,
        }),

    staffPerformance: (params?: DateRangeParams) =>
        api.get<StaffPerformanceItem[]>('/reports/staff/performance', {
            params,
        }),

    deliveryPerformance: (params?: DateRangeParams) =>
        api.get<DeliveryPerformance>('/reports/delivery/performance', {
            params,
        }),

    driverPerformance: (params?: DateRangeParams) =>
        api.get<DriverPerformanceItem[]>('/reports/delivery/by-driver', {
            params,
        }),

    movementByTime: (
        params?: DateRangeParams & {
            group_by?: 'day' | 'week' | 'month' | 'year'
        },
    ) =>
        api.get<MovementByTimeItem[]>('/reports/inventory/movement-by-time', {
            params,
        }),

    /** Phase X — chart breakdown theo SP cho phép tick từng SP.
     *  Trả rows (mỗi period có total + map breakdown by product_id) +
     *  top_entities (top N by total movement, default 10). */
    movementBreakdown: (
        params?: DateRangeParams & {
            group_by?: 'day' | 'week' | 'month' | 'year'
            top_n?: number
        },
    ) =>
        api.get<MovementBreakdownResult>(
            '/reports/inventory/movement-breakdown',
            { params },
        ),

    /** Approved discarded product lots aggregated by period × product. */
    defects: (
        params?: DateRangeParams & {
            group_by?: 'day' | 'week' | 'month' | 'year'
        },
    ) => {
        const { date_from, date_to, group_by } = params ?? {}
        return api.get<DefectItem[]>('/reports/inventory/defects', {
            params: params ? { date_from, date_to, group_by } : undefined,
        })
    },

    /** Top products by absolute stock movement in date range. Requires warehouse_sales.read. */
    topMovers: (params?: DateRangeParams & { limit?: number }) =>
        api.get<TopMoverItem[]>('/reports/inventory/top-movers', {
            params,
        }),

    /**
     * `status`/`search` chỉ dùng cho `type: 'inventory'` — file tải về phải
     * khớp đúng bộ lọc đang hiện, không phải toàn bộ kho.
     */
    exportExcelUrl: (
        params: DateRangeParams & {
            type?: string
            status?: InventoryStockFilter
            search?: string
        },
    ) => {
        // Dev đi qua proxy của Vite nên `API_BASE_URL` là đường dẫn tương đối
        // (`/api/v1`) — thiếu base thì `new URL()` ném lỗi và nút xuất chết câm.
        const url = new URL(
            `${API_BASE_URL}/reports/export/excel`,
            window.location.origin,
        )
        if (params.date_from)
            url.searchParams.set('date_from', params.date_from)
        if (params.date_to) url.searchParams.set('date_to', params.date_to)
        if (params.type) url.searchParams.set('type', params.type)
        if (params.status) url.searchParams.set('status', params.status)
        if (params.search) url.searchParams.set('search', params.search)
        return url.toString()
    },
}
